'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { isPosthogEnabled } from '../../utils/is-posthog-enabled';
import { type FeatureFlagResolvedState, featureFlagResolved } from '../types/feature-flag-resolution';
import { resolveFeatureFlagsErrorsLoading } from './feature-flag-load-state';

/**
 * usePostHogFeatureFlag — PostHog implementation of client-side feature flag evaluation.
 *
 * Environment: client-only (requires PostHog React context in the component tree).
 * Used by: observability/feature-flags/use-is-feature-enabled.ts
 *
 * To replace this hook: create a new hook in this providers/ folder and update
 * the import in use-is-feature-enabled.ts. No other files need to change.
 *
 * @param flagName - The feature flag name to check
 * @returns true if enabled, false otherwise (or while loading)
 */
export function usePostHogFeatureFlag(flagName) {
  return usePostHogFeatureFlagResolved(flagName).enabled;
}

/**
 * Tri-state variant: exposes both the enabled boolean and a resolution
 * signal so callers that initiate side effects can wait for PostHog to
 * settle before firing. While `isResolved` is false, `enabled` is
 * always false (hydration-consistent with the server render).
 *
 * Use this — not `usePostHogFeatureFlag` — anywhere the act of reading
 * the flag races against a side effect (OAuth redirect, signIn.create,
 * mutation). Pure render gates (skeleton, maintenance card) can stick
 * with the boolean variant.
 */
export function usePostHogFeatureFlagResolved(flagName, options) {
  const $ = _c(17);
  const failOpenAfterMs = options?.failOpenAfterMs;
  const [mounted, setMounted] = useState(false);
  const [failedOpen, setFailedOpen] = useState(false);
  const [flagsLoaded, setFlagsLoaded] = useState(null);
  const flagValue = useFeatureFlagEnabled(flagName);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setMounted(true);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[2] !== mounted) {
    t2 = () => {
      if (!mounted || !isPosthogEnabled()) {
        return;
      }
      return posthog.onFeatureFlags((_flags, _variants, context) => {
        setFlagsLoaded({
          errorsLoading: resolveFeatureFlagsErrorsLoading(context)
        });
      });
    };
    t3 = [mounted];
    $[2] = mounted;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== failOpenAfterMs) {
    t4 = () => {
      if (failOpenAfterMs === undefined) {
        return;
      }
      const timer = setTimeout(() => setFailedOpen(true), failOpenAfterMs);
      return () => clearTimeout(timer);
    };
    t5 = [failOpenAfterMs];
    $[5] = failOpenAfterMs;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  if (!mounted) {
    let t6;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        enabled: false,
        isResolved: false
      };
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  if (!isPosthogEnabled()) {
    let t6;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = featureFlagResolved(false, "disabled");
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  if (flagsLoaded?.errorsLoading) {
    let t6;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = featureFlagResolved(false, "error");
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    return t6;
  }
  if (flagsLoaded) {
    const t6 = flagValue === true;
    let t7;
    if ($[11] !== t6) {
      t7 = featureFlagResolved(t6, "resolved");
      $[11] = t6;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  if (flagValue !== undefined) {
    let t6;
    if ($[13] !== flagValue) {
      t6 = featureFlagResolved(flagValue, "resolved");
      $[13] = flagValue;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    return t6;
  }
  if (failedOpen) {
    let t6;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = featureFlagResolved(false, "timeout");
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    return t6;
  }
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      enabled: false,
      isResolved: false
    };
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}